/**
 * Output grids for a selected box.
 *
 * Grids are aligned with lines of longitude and latitude (so every source tile maps onto a
 * plain rectangle of cells) but sized so cells are square in metres. At 60°N a degree of
 * longitude is half a degree of latitude; a grid square in degrees would stretch the terrain.
 */

import { createProjection, type LngLatBounds } from '@/lib/virtual-course/projection'

/** Largest box side we generate, so every request stays quick and cheap. */
export const MAX_SIDE_KM = 120

export interface GridSpec {
  bounds: LngLatBounds
  cols: number
  rows: number
  /** Cell size in metres (the same east–west and north–south, to within rounding). */
  cellM: number
  /** Box size in metres. */
  widthM: number
  heightM: number
}

export interface Grid<T extends ArrayLike<number>> extends GridSpec {
  /** Row-major from the north-west corner. */
  values: T
}

export interface BoxSize {
  widthM: number
  heightM: number
}

export function boxSize(bounds: LngLatBounds): BoxSize {
  const projection = createProjection({
    latitude: (bounds.north + bounds.south) / 2,
    longitude: (bounds.east + bounds.west) / 2,
  })
  return {
    widthM: (bounds.east - bounds.west) * projection.metresPerDegreeLng,
    heightM: (bounds.north - bounds.south) * projection.metresPerDegreeLat,
  }
}

export type BoxProblem = 'empty' | 'too-large' | 'polar'

export function validateBox(bounds: LngLatBounds): BoxProblem | null {
  if (!(bounds.east > bounds.west && bounds.north > bounds.south)) return 'empty'
  // GLO-30 and WorldCover both stop short of the poles; past 84° there is nothing to read.
  if (bounds.north > 84 || bounds.south < -84) return 'polar'
  const { widthM, heightM } = boxSize(bounds)
  if (widthM < 200 || heightM < 200) return 'empty'
  if (widthM > MAX_SIDE_KM * 1000 || heightM > MAX_SIDE_KM * 1000) return 'too-large'
  return null
}

/**
 * A grid at `cellM` metres, coarsened evenly on both axes when the longer side would need
 * more than `maxCells` cells.
 */
export function gridForBounds(bounds: LngLatBounds, cellM: number, maxCells: number): GridSpec {
  const { widthM, heightM } = boxSize(bounds)
  const effectiveCell = Math.max(cellM, Math.max(widthM, heightM) / maxCells)
  return {
    bounds,
    cols: Math.max(2, Math.round(widthM / effectiveCell)),
    rows: Math.max(2, Math.round(heightM / effectiveCell)),
    cellM: effectiveCell,
    widthM,
    heightM,
  }
}

export interface CellWindow {
  /** First and last output column and row (inclusive) whose centres fall in the tile. */
  col0: number
  col1: number
  row0: number
  row1: number
  /** Geographic extent of exactly those cells: [west, south, east, north]. */
  bbox: [number, number, number, number]
}

/** The block of output cells whose centres fall inside a tile, or null when none do. */
export function cellWindow(grid: GridSpec, tile: LngLatBounds): CellWindow | null {
  const { bounds, cols, rows } = grid
  const dLng = (bounds.east - bounds.west) / cols
  const dLat = (bounds.north - bounds.south) / rows
  const col0 = Math.max(0, Math.ceil((tile.west - bounds.west) / dLng - 0.5))
  const col1 = Math.min(cols - 1, Math.ceil((tile.east - bounds.west) / dLng - 0.5) - 1)
  const row0 = Math.max(0, Math.ceil((bounds.north - tile.north) / dLat - 0.5))
  const row1 = Math.min(rows - 1, Math.ceil((bounds.north - tile.south) / dLat - 0.5) - 1)
  if (col1 < col0 || row1 < row0) return null
  return {
    col0,
    col1,
    row0,
    row1,
    bbox: [
      bounds.west + col0 * dLng,
      bounds.north - (row1 + 1) * dLat,
      bounds.west + (col1 + 1) * dLng,
      bounds.north - row0 * dLat,
    ],
  }
}

/** Copy a tile's block of cells into the stitched grid. */
export function writeWindow(target: Grid<Float32Array | Uint8Array>, window: CellWindow, block: ArrayLike<number>): void {
  const width = window.col1 - window.col0 + 1
  for (let row = window.row0; row <= window.row1; row += 1) {
    const sourceOffset = (row - window.row0) * width
    const targetOffset = row * target.cols + window.col0
    for (let column = 0; column < width; column += 1) {
      target.values[targetOffset + column] = block[sourceOffset + column]
    }
  }
}

/** Nearest cell for a position given as fractions across the box (0 at west / north). */
export function cellAt(grid: GridSpec, u: number, v: number): number {
  const column = Math.min(grid.cols - 1, Math.max(0, Math.floor(u * grid.cols)))
  const row = Math.min(grid.rows - 1, Math.max(0, Math.floor(v * grid.rows)))
  return row * grid.cols + column
}

/** Bilinear sample between cell centres, for heights. */
export function sampleBilinear(grid: Grid<Float32Array>, u: number, v: number): number {
  const x = Math.min(grid.cols - 1, Math.max(0, u * grid.cols - 0.5))
  const y = Math.min(grid.rows - 1, Math.max(0, v * grid.rows - 0.5))
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const x1 = Math.min(grid.cols - 1, x0 + 1)
  const y1 = Math.min(grid.rows - 1, y0 + 1)
  const fx = x - x0
  const fy = y - y0
  const at = (column: number, row: number) => grid.values[row * grid.cols + column]
  const top = at(x0, y0) * (1 - fx) + at(x1, y0) * fx
  const bottom = at(x0, y1) * (1 - fx) + at(x1, y1) * fx
  return top * (1 - fy) + bottom * fy
}

/** Steepness in degrees per height cell, from central differences. */
export function slopeDegrees(heights: Grid<Float32Array>): Grid<Float32Array> {
  const { cols, rows, values } = heights
  const cellX = heights.widthM / cols
  const cellY = heights.heightM / rows
  const slope = new Float32Array(cols * rows)
  const at = (column: number, row: number) =>
    values[Math.min(rows - 1, Math.max(0, row)) * cols + Math.min(cols - 1, Math.max(0, column))]
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < cols; column += 1) {
      const dx = (at(column + 1, row) - at(column - 1, row)) / (2 * cellX)
      const dy = (at(column, row + 1) - at(column, row - 1)) / (2 * cellY)
      slope[row * cols + column] = (Math.atan(Math.hypot(dx, dy)) * 180) / Math.PI
    }
  }
  return { ...heights, values: slope }
}
